"use client";

import { useMemo, useState } from "react";
import { IconChevronDownOutline18, IconChevronRightOutline18 } from "nucleo-ui-outline-18";
import type { WorkflowNodeExecutionState } from "@/stores/workflowRunsStore";
import { splitResult } from "./primary-content";
import { humanizeKey } from "./humanize";
import { registerAllRenderers } from "./renderers";
import { RegistryValue } from "./renderers/RegistryValue";
import { StatGroupRenderer } from "./renderers/StatGroupRenderer";
import type { RenderContext } from "./types";

// Register renderers once at module load (idempotent).
registerAllRenderers();
export interface AutoResultViewProps {
  value: Record<string, unknown>;
  /** Node type, used as a detection hint. */
  nodeType?: string;
  nodeId?: string;
  executionState?: Record<string, WorkflowNodeExecutionState>;
}

/** Rich, content-first renderer for a workflow node result. */
export function AutoResultView({
  value,
  nodeType = "",
  nodeId = "",
  executionState = {}
}: AutoResultViewProps) {
  const split = useMemo(() => splitResult(value, nodeType), [value, nodeType]);
  const [metadataOpen, setMetadataOpen] = useState(split.metadataOnly);
  const baseCtx = (key: string | undefined, parent: Record<string, unknown> | undefined): RenderContext => ({
    nodeType: nodeType || undefined,
    nodeId,
    key,
    path: key != null ? [key] : [],
    depth: 0,
    parent,
    executionState
  });
  if (split.isEmpty) {
    return <p className="text-ds-text-muted py-2 text-xs">No output</p>;
  }

  // An `error` string is surfaced as a callout above everything else.
  const errorEntry = split.body.find(e => e.key === "error" && typeof e.value === "string");
  const body = split.body.filter(e_0 => e_0 !== errorEntry);
  return <div className="space-y-3 text-sm">
      {errorEntry && <div className="bg-ds-error-surface text-ds-error-text rounded-md px-3 py-2 text-xs">
          {errorEntry.value as string}
        </div>}

      {split.headline && <div>
          <RegistryValue value={split.headline.value} ctx={baseCtx(split.headline.key, value)} />
        </div>}

      {body.length > 0 && <div className="space-y-1.5">
          {body.map(({
        key: key_0,
        value: v
      }) => <div key={key_0} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
              <span className="text-ds-text-muted shrink-0 font-medium sm:w-40">{humanizeKey(key_0)}</span>
              <div className="min-w-0 flex-1">
                <RegistryValue value={v} ctx={baseCtx(key_0, value)} />
              </div>
            </div>)}
        </div>}

      {split.metadata.length > 0 && <div className="border-ds-border-default border-t pt-2">
          <button onClick={() => setMetadataOpen(o => !o)} className="text-ds-text-muted hover:text-ds-text-primary flex items-center gap-1 text-xs font-medium">
            {metadataOpen ? <IconChevronDownOutline18 className="h-3.5 w-3.5" /> : <IconChevronRightOutline18 className="h-3.5 w-3.5" />}
            Metadata ({split.metadata.length})
          </button>
          {metadataOpen && <div className="mt-1.5">
              <StatGroupRenderer.Component value={Object.fromEntries(split.metadata.map(e_1 => [e_1.key, e_1.value]))} ctx={baseCtx(undefined, value)} />
            </div>}
        </div>}
    </div>;
}